<template lang="pug">
include /mixins

+b.ti-stripe-view(
  style='padding: 20px'
)
  v-caption(size='5' variant='bold' style='padding-bottom: 20px') {{ _("c Stripe payment") }}
  +e.content
    +e.loader
    +e.form
      form#payment-form.form-loader
        #error-message
        +e.load-error(v-if='loadError')
          p(style='color: red') {{ loadError }}
          +b.control-button--variant_success.--border_whole.BUTTON(
            style='margin-top: 12px'
            type='button'
            @click='start'
          )
            +e.element--size_lg
              +b.caption--size_4.--variant_bold.SPAN {{ _('Try again') }}
        +e.load-error(v-if='timerExpired')
          p(style='color: red') {{ _('Your reservation time has expired. Please return to the cart and select your tickets again.') }}
        #payment-element
          // Elements will create form elements here
        div#payment-request-button
        //- hidden instead of disabled: a disabled button swallows clicks without any event to log
        +e.button(v-show='!loadError && !timerExpired')
          .card__panel.card__panel--offset_bottom
            +b.row--justification_end
              +b.cell.cols--12.--6-sm.--12-md.--6-xl
                +b.control-button--variant_success.--border_whole.BUTTON#submit(
                  style='margin-top: 12px'
                  type='submit'
                )
                  +e.element--size_lg
                    +b.caption--size_4.--variant_bold.SPAN {{ _('Pay now') }}

</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'

import Checkbox from '@cabinet/components/forms/blocks/Checkbox'
import { successHandler, errorHandler } from '@cabinet/util'
import { Card, createToken } from 'vue-stripe-elements-plus'
import AutocompleteMixin from './../../../mixin/AutocompleteMixin'
import StripeBtn from '@cabinet/components/forms/StripeBtn'

import OrdersService from '@cabinet/services/api/orders'
import PaymentsService from '@cabinet/services/api/payments'
import UiSelect from '@cabinet/components/forms/blocks/UiSelect'
import UiCreditCardView from '@cabinet/components/forms/UiCreditCardView'

import StripePayment, { createCheckoutTrace } from '@cabinet/packages/stripe/src/main'
// import StripePayment from '@cabinet/packages/stripe/src/main'

export default {
  components: {
    UiCreditCardView,
    Checkbox,
    StripeCard: Card,
    StripeBtn,
    UiSelect,
  },

  props: {
    dataInfo: {
      type: Object
    },
    formData: {
      type: Object
    },
    totalPrice: {
      required: true
    },
    currency: {
      required: false,
      default: 'USD'
    },
    order: {},
    redirect: {},
    promise: {},
    total: {},
  },
  data() {
    return {
      complete: false,
      loading: false,
      loadError: '',
    }
  },
  created() {
    // one trace per modal opening: all steps of this payment attempt are logged under it
    this.trace = createCheckoutTrace(this.order.id)
  },
  mounted() {
    // raw cart timer value, to see later how much reservation time was left
    this.trace.info('modal_open', `timer=${this.getCurrentTimer}`)
    if (this.timerExpired) this.reportTimerExpired()
    this.start()
  },
  beforeDestroy() {
    this.trace.info('modal_closed', 'Payment modal closed')
    if (this.stripeInstance) this.stripeInstance.destroy()
  },
  computed: {
    ...mapState('userModule', ['userData']),
    ...mapGetters('cartModule', ['getCurrentTimer']),
    totalPriceConverted() {
      return parseFloat(this.total) * 100
    },
    timerExpired() {
      return !(this.getCurrentTimer > 0)
    },
  },
  watch: {
    // not immediate: the trace is created in `created`, after immediate watchers run
    timerExpired(expired) {
      if (expired) this.reportTimerExpired()
    },
  },
  methods: {
    reportTimerExpired() {
      this.trace.report('timer_expired', `timer=${this.getCurrentTimer}`)
    },
    // creates the intent once, then (re)loads the Stripe form for it; also used by "Try again"
    start() {
      this.loadError = ''
      const form = this.$el.querySelector('#payment-form')
      form.classList.add('form-loader')
      const ready = this.stripeInstance ? this.stripeInstance.loadScript() : this.createIntent()
      ready.then(() => {
        form.classList.remove('form-loader')
      }, err => {
        form.classList.remove('form-loader')
        const data = err.response && err.response.data
        this.loadError = (data && (data.error || data.detail)) ||
          this._('Payment form failed to load. Please check your connection and try again.')
        this.trace.report(
          this.stripeInstance ? 'stripe' : 'intent',
          // the response body holds the validation details (which field failed and why)
          err.response ? `HTTP ${err.response.status} ${JSON.stringify(data)}` : err.message,
        )
        // the order is already paid: show its confirmation instead of the payment form
        if (err.response && err.response.status === 409 && data.order_redirect) {
          window.location.assign(data.order_redirect)
        }
      })
    },
    createIntent() {
      return PaymentsService.createIntent(
        {
          currency: this.currency == "CA$" ? 'CAD' : 'USD', 
          amount: this.totalPriceConverted, 
          payment_parameters: {
            setup_future_usage: 'off_session',
            automatic_payment_methods: { enabled: true },
            confirm: false,
            payment_method_options: {
              'card': {
                'setup_future_usage': 'off_session',
              },
            }, 
          } ,
          "automatic_payment_methods": [
            "card",
          ],
          referer: window.location.href,
          cart: this.order.id,
          provider: this.order.provider,
        }
      ).then(({ data: { client_secret, payment } } ) => {
        // the PaymentIntent id is the part of the client secret before "_secret_"; the secret itself is not logged
        this.trace.payment = payment
        this.trace.info('intent_created', `PaymentIntent ${client_secret.split('_secret_')[0]} created`)
        this.stripeInstance = new StripePayment(this.totalPriceConverted, this.dataInfo.token, client_secret, this.order.id, 'payment-form', payment, this.redirect, {...this.formData, ...this.dataInfo}, this.trace)
        if (this.only_secure) {
          this.opened = true
          return this.stripeInstance.loadSecureScript(this.order)
        }
        this.stripeInstance.complete()
        this.opened = true
        return this.stripeInstance.loadScript()
      })
    },
  }
}
</script>
